import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Stack } from "@mui/material";
import { tokens } from "../../theme";

/**
 * Grouped columns for per-period magnitudes.
 *
 * Discrete monthly buckets being compared against each other is a magnitude
 * question, not a trend — so columns, not lines. A line between March and April
 * would imply the value passed through the days in between, which for a monthly
 * total it never does.
 *
 * Hover is per band rather than per column: the reader aims at a month, and a
 * hit target the width of the band is far easier to land than a 20px bar.
 */

const PAD = { top: 16, right: 12, bottom: 22, left: 38 };
const MAX_COL = 24; // columns never fill their slot; the leftover is air
const GAP = 2; // surface gap between paired columns

const ColumnChart = ({
  points = [],
  series = [],
  height = 170,
  yMax,
  formatValue = (v) => `${v}`,
}) => {
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(640);
  const [hoverIndex, setHoverIndex] = useState(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setWidth(Math.max(280, Math.floor(w)));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const plotW = Math.max(1, width - PAD.left - PAD.right);
  const plotH = Math.max(1, height - PAD.top - PAD.bottom);

  const top = useMemo(() => {
    if (yMax) return yMax;
    const peak = Math.max(0, ...series.flatMap((s) => s.values.map((v) => v || 0)));
    return Math.max(1, Math.ceil(peak * 1.15));
  }, [yMax, series]);

  const ticks = useMemo(() => {
    const raw = top / 4;
    const mag = Math.pow(10, Math.floor(Math.log10(Math.max(raw, 1))));
    const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) || mag * 10;
    const out = [];
    for (let v = 0; v <= top + 1e-9; v += step) out.push(Math.round(v * 100) / 100);
    return out;
  }, [top]);

  const band = plotW / Math.max(1, points.length);
  const colW = Math.min(MAX_COL, Math.max(3, (band - GAP * (series.length + 1)) / series.length));
  const yAt = useCallback((v) => PAD.top + plotH - (Math.max(0, v) / top) * plotH, [plotH, top]);
  const baseline = PAD.top + plotH;

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left - PAD.left;
    const i = Math.floor(x / band);
    setHoverIndex(i >= 0 && i < points.length ? i : null);
  };

  if (!points.length) return null;

  const hovered = hoverIndex === null ? null : points[hoverIndex];
  const flip = hoverIndex !== null && PAD.left + hoverIndex * band > width * 0.6;

  return (
    <Box ref={wrapRef} sx={{ position: "relative", width: "100%" }}>
      <svg
        width={width}
        height={height}
        role="img"
        aria-label={`${series.map((s) => s.label).join(" and ")} by month`}
        onPointerMove={onMove}
        onPointerLeave={() => setHoverIndex(null)}
        style={{ display: "block", touchAction: "none" }}
      >
        {ticks.map((t) => (
          <g key={t}>
            <line
              x1={PAD.left}
              x2={width - PAD.right}
              y1={yAt(t)}
              y2={yAt(t)}
              stroke={tokens.border}
              strokeWidth="1"
            />
            <text x={PAD.left - 8} y={yAt(t) + 3} textAnchor="end" fontSize="9" fill={tokens.textMuted}>
              {t}
            </text>
          </g>
        ))}

        {hoverIndex !== null && (
          <rect
            x={PAD.left + hoverIndex * band}
            y={PAD.top}
            width={band}
            height={plotH}
            fill={tokens.surfaceSubtle}
          />
        )}

        {points.map((p, i) => {
          const groupW = series.length * colW + (series.length - 1) * GAP;
          const startX = PAD.left + i * band + (band - groupW) / 2;
          return (
            <g key={p.x}>
              {series.map((s, si) => {
                const v = s.values[i] || 0;
                const x = startX + si * (colW + GAP);
                const y = yAt(v);
                const h = baseline - y;
                if (h <= 0) return null;
                // rounded at the data end, square at the baseline
                const r = Math.min(4, colW / 2, h);
                return (
                  <path
                    key={s.key}
                    d={`M${x},${baseline} L${x},${y + r} Q${x},${y} ${x + r},${y} L${x + colW - r},${y} Q${x + colW},${y} ${x + colW},${y + r} L${x + colW},${baseline} Z`}
                    fill={s.color}
                    opacity={hoverIndex === null || hoverIndex === i ? 1 : 0.55}
                  />
                );
              })}
            </g>
          );
        })}

        <line
          x1={PAD.left}
          x2={width - PAD.right}
          y1={baseline}
          y2={baseline}
          stroke={tokens.border}
          strokeWidth="1"
        />

        {points.map((p, i) => {
          // label every other month when they'd otherwise collide
          const skip = band < 34 && i % 2 === 1;
          if (skip) return null;
          return (
            <text
              key={`lab-${p.x}`}
              x={PAD.left + i * band + band / 2}
              y={height - 6}
              textAnchor="middle"
              fontSize="9"
              fill={tokens.textMuted}
            >
              {p.label}
            </text>
          );
        })}
      </svg>

      {hovered && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: flip ? "auto" : `${PAD.left + hoverIndex * band + band}px`,
            right: flip ? `${width - PAD.left - hoverIndex * band}px` : "auto",
            pointerEvents: "none",
            background: tokens.card,
            border: `1px solid ${tokens.border}`,
            borderRadius: "8px",
            boxShadow: "0 4px 12px -4px rgba(42,29,34,0.16)",
            p: "0.5rem 0.75rem",
            minWidth: "7rem",
            zIndex: 2,
          }}
        >
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: 0.5 }}>{hovered.label}</Box>
          {series.map((s) => (
            <Stack key={s.key} direction="row" alignItems="center" spacing={0.75}>
              <Box sx={{ width: "8px", height: "8px", borderRadius: "6px", background: s.color }} />
              <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tokens.text }}>
                {formatValue(s.values[hoverIndex] || 0)}
              </Box>
              <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>{s.label}</Box>
            </Stack>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ColumnChart;
